import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { useAuth } from "../context/AuthContext";
import { api, inr, errMsg } from "../lib/api";
import { PageShell, Loader } from "../components/SiteChrome";

const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const STATUSES = ["created", "paid", "shipped", "delivered", "failed"];

function RevenueChart({ series }) {
  const data = series.map((d) => ({ ...d, label: new Date(d.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) }));
  return (
    <div className="chart-card" data-testid="revenue-chart">
      <div className="chart-head">
        <span className="pg-kicker" style={{ margin: 0 }}>Revenue — last 30 days</span>
        <span style={{ fontSize: 11, color: "var(--ink-dim)", letterSpacing: ".08em" }}>
          {inr(data.reduce((s, d) => s + d.revenue, 0))} in this window
        </span>
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <AreaChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#D9A441" stopOpacity={0.34} />
              <stop offset="100%" stopColor="#D9A441" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="rgba(233,220,196,.09)" vertical={false} />
          <XAxis dataKey="label" tick={{ fill: "#B9A88E", fontSize: 10, letterSpacing: 1 }}
            axisLine={{ stroke: "rgba(233,220,196,.16)" }} tickLine={false} minTickGap={28} />
          <YAxis tick={{ fill: "#B9A88E", fontSize: 10 }} axisLine={false} tickLine={false} width={44}
            tickFormatter={(v) => (v >= 1000 ? `₹${(v / 1000).toFixed(1)}k` : `₹${v}`)} />
          <Tooltip
            contentStyle={{ background: "#1D130E", border: "1px solid rgba(233,220,196,.16)", borderRadius: 10,
              fontSize: 12, color: "#F5EDE0" }}
            labelStyle={{ color: "#B9A88E", fontSize: 10, letterSpacing: 1, textTransform: "uppercase" }}
            formatter={(v, name) => (name === "revenue" ? [inr(v), "Revenue"] : [v, "Paid orders"])}
          />
          <Area type="monotone" dataKey="revenue" stroke="#D9A441" strokeWidth={1.6}
            fill="url(#goldFill)" activeDot={{ r: 3.5, fill: "#E9C27C", stroke: "#150E0A" }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function FlavourSplit({ split }) {
  const total = split.reduce((s, f) => s + f.units, 0);
  const leader = split[0];
  return (
    <div className="chart-card" style={{ margin: 0 }} data-testid="flavour-split">
      <div className="chart-head">
        <span className="pg-kicker" style={{ margin: 0 }}>Best seller</span>
        {total > 0 && <span style={{ fontSize: 11, color: "var(--gold-soft)", letterSpacing: ".08em" }} data-testid="flavour-leader">
          {leader.name} leads
        </span>}
      </div>
      {total === 0 ? (
        <p style={{ color: "var(--ink-dim)", fontSize: 13, padding: "40px 6px", textAlign: "center" }} data-testid="flavour-empty">
          No paid orders yet — the race hasn't started.
        </p>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={150}>
            <PieChart>
              <Pie data={split} dataKey="units" nameKey="name" cx="50%" cy="50%"
                innerRadius={44} outerRadius={64} paddingAngle={3} stroke="#150E0A" strokeWidth={2}>
                {split.map((f) => <Cell key={f.product_id} fill={f.sw} />)}
              </Pie>
              <Tooltip
                contentStyle={{ background: "#1D130E", border: "1px solid rgba(233,220,196,.16)", borderRadius: 10,
                  fontSize: 12, color: "#F5EDE0" }}
                formatter={(v, name) => [`${v} tins · ${Math.round((v / total) * 100)}%`, name]}
              />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ padding: "4px 6px 12px" }}>
            {split.map((f) => (
              <div className="co-line" key={f.product_id} style={{ fontSize: 13 }} data-testid={`flavour-row-${f.product_id}`}>
                <span style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <span style={{ width: 11, height: 11, borderRadius: "50%", background: f.sw, flex: "none" }} />
                  {f.name}
                </span>
                <span style={{ color: "var(--ink-dim)" }}>
                  {f.units} tins · {Math.round((f.units / total) * 100)}% · <span style={{ color: "var(--cream)" }}>{inr(f.revenue)}</span>
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Overview({ stats }) {
  return (
    <>
      <div className="stats" style={{ marginTop: 0 }}>
        <div className="stat"><b data-testid="stat-revenue">{inr(stats.revenue)}</b><span>Revenue</span></div>
        <div className="stat"><b data-testid="stat-orders">{stats.orders_paid}<small>/{stats.orders_total}</small></b><span>Paid / total orders</span></div>
        <div className="stat"><b data-testid="stat-customers">{stats.customers}</b><span>Customers</span></div>
        <div className="stat"><b data-testid="stat-units">{stats.units_sold}</b><span>Tins sold</span></div>
      </div>
      {stats.revenue_series && (
        <div className="chart-grid">
          <RevenueChart series={stats.revenue_series} />
          <FlavourSplit split={stats.flavour_split || []} />
        </div>
      )}
      <h3 style={{ fontFamily: "var(--display)", fontWeight: 400, color: "var(--cream)", margin: "34px 0 14px" }}>Recent orders</h3>
      <div className="atable-wrap">
        <table className="atable">
          <thead><tr><th>Order</th><th>Customer</th><th>Items</th><th>Amount</th><th>Status</th></tr></thead>
          <tbody>
            {stats.recent_orders.map((o) => (
              <tr key={o.order_id}>
                <td>{o.order_id}<br /><span style={{ fontSize: 11, color: "var(--ink-dim)" }}>{fmt(o.created_at)}</span></td>
                <td>{o.name}<br /><span style={{ fontSize: 11, color: "var(--ink-dim)" }}>{o.email}</span></td>
                <td>{o.items.map((i) => `${i.name}×${i.qty}`).join(", ")}</td>
                <td>{inr(o.amount)}</td>
                <td><span className={`st ${o.status}`}>{o.status}</span></td>
              </tr>
            ))}
            {stats.recent_orders.length === 0 && <tr><td colSpan={5} style={{ color: "var(--ink-dim)" }}>No orders yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Orders() {
  const [orders, setOrders] = useState(null);
  const load = useCallback(() => api.get("/admin/orders").then(({ data }) => setOrders(data)), []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => {
    await api.patch(`/admin/orders/${id}/status`, { status });
    load();
  };
  if (!orders) return <Loader />;
  return (
    <div className="atable-wrap">
      <table className="atable" data-testid="admin-orders-table">
        <thead><tr><th>Order</th><th>Customer</th><th>Ship to</th><th>Items</th><th>Amount</th><th>Status</th></tr></thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.order_id} data-testid={`admin-order-${o.order_id}`}>
              <td>{o.order_id}<br /><span style={{ fontSize: 11, color: "var(--ink-dim)" }}>{fmt(o.created_at)}</span></td>
              <td>{o.name}<br /><span style={{ fontSize: 11, color: "var(--ink-dim)" }}>{o.email}<br />{o.phone}</span></td>
              <td style={{ fontSize: 12 }}>{o.address}, {o.city} {o.pincode}</td>
              <td>{o.items.map((i) => `${i.name}×${i.qty}`).join(", ")}</td>
              <td>{inr(o.amount)}</td>
              <td>
                <select value={o.status} onChange={(e) => setStatus(o.order_id, e.target.value)} data-testid={`admin-order-status-${o.order_id}`}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </td>
            </tr>
          ))}
          {orders.length === 0 && <tr><td colSpan={6} style={{ color: "var(--ink-dim)" }}>No orders yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Customers() {
  const [users, setUsers] = useState(null);
  useEffect(() => { api.get("/admin/customers").then(({ data }) => setUsers(data)); }, []);
  if (!users) return <Loader />;
  return (
    <div className="atable-wrap">
      <table className="atable" data-testid="admin-customers-table">
        <thead><tr><th>Name</th><th>Email</th><th>Joined</th><th>Paid orders</th></tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.user_id}>
              <td>{u.name || "—"}</td><td>{u.email}</td>
              <td>{u.created_at ? fmt(u.created_at) : "—"}</td><td>{u.orders}</td>
            </tr>
          ))}
          {users.length === 0 && <tr><td colSpan={4} style={{ color: "var(--ink-dim)" }}>No customers yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Stock() {
  const [products, setProducts] = useState(null);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => api.get("/products").then(({ data }) => setProducts(data)), []);
  useEffect(() => { load(); }, [load]);
  const save = async (p, field, value) => {
    try {
      await api.patch(`/admin/products/${p.product_id}`, { [field]: Number(value) });
      setMsg(`${p.name} updated.`);
      load();
    } catch (e) { setMsg(errMsg(e)); }
  };
  if (!products) return <Loader />;
  return (
    <>
      <div className="atable-wrap">
        <table className="atable" data-testid="admin-stock-table">
          <thead><tr><th>Product</th><th>Price (₹)</th><th>Stock</th></tr></thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.product_id}>
                <td>{p.name}</td>
                <td><input type="number" min="1" defaultValue={p.price}
                  onBlur={(e) => Number(e.target.value) !== p.price && save(p, "price", e.target.value)}
                  data-testid={`admin-price-${p.product_id}`} /></td>
                <td><input type="number" min="0" defaultValue={p.stock}
                  onBlur={(e) => Number(e.target.value) !== p.stock && save(p, "stock", e.target.value)}
                  data-testid={`admin-stock-${p.product_id}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="form-msg ok" style={{ marginTop: 10 }} data-testid="admin-stock-msg">{msg}</p>
    </>
  );
}

function Messages() {
  const [msgs, setMsgs] = useState(null);
  useEffect(() => { api.get("/admin/messages").then(({ data }) => setMsgs(data)); }, []);
  if (!msgs) return <Loader />;
  return (
    <div className="atable-wrap">
      <table className="atable">
        <thead><tr><th>From</th><th>Message</th><th>When</th></tr></thead>
        <tbody>
          {msgs.map((m) => (
            <tr key={m.message_id}><td>{m.name}<br /><span style={{ fontSize: 11, color: "var(--ink-dim)" }}>{m.email}</span></td>
              <td style={{ maxWidth: 420, whiteSpace: "pre-wrap" }}>{m.message}</td><td>{fmt(m.created_at)}</td></tr>
          ))}
          {msgs.length === 0 && <tr><td colSpan={3} style={{ color: "var(--ink-dim)" }}>No messages yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

const TABS = ["Overview", "Orders", "Customers", "Stock", "Messages"];

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Overview");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user === false) navigate("/login");
    if (user && user.role !== "admin") navigate("/account");
  }, [user, navigate]);

  useEffect(() => {
    if (user && user.role === "admin")
      api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, [user, tab]);

  if (!user || user.role !== "admin") return <PageShell><Loader /></PageShell>;

  return (
    <PageShell kicker="Admin" title="The counting room." sub="Orders, revenue, customers and stock — all of it, only for you.">
      <div className="adm-wrap" data-testid="admin-dashboard">
        <div className="adm-tabs">
          {TABS.map((t) => (
            <button key={t} className={tab === t ? "on" : ""} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase()}`}>{t}</button>
          ))}
        </div>
        {tab === "Overview" && (stats ? <Overview stats={stats} /> : <Loader />)}
        {tab === "Orders" && <Orders />}
        {tab === "Customers" && <Customers />}
        {tab === "Stock" && <Stock />}
        {tab === "Messages" && <Messages />}
      </div>
    </PageShell>
  );
}
